import { lazy } from 'react';
import { ArticleList } from '../article/components/ArticleList/ArticleList';
import { ArticleCardBlock } from '../article/components/ArticleCard/ArticleCardBlock';
import { ArticleHighlightedCard } from '../article/components/ArticleHighlightedCard/ArticleHighlightedCard';
import { FixtureList } from '../fixtures/components/FixtureList/FixtureList';
import SponsorCard from '../league/components/SponsorCard/SponsorCard';
import { Ad } from '../ads/components/Ad/Ad';
import Link from '../common/components/Link/Link';
import { CmsComponent } from './contracts';
import { SponsorsGrid } from '../league/components/SponsorsGrid/SponsorsGrid';
// Static, not lazy: `apps/web` server-renders with the synchronous `renderToString`, so a lazy
// component emits Row.tsx's 200px Suspense placeholder into the server HTML instead of the
// image. Imported from the module rather than the barrel, which also pulls in the ConfigForm.
import { ImageBlock } from '../common/components/Image/ImageBlock';
// Imported from the module rather than the `Divider` barrel on purpose: the barrel also
// re-exports DividerConfigForm, which pulls @configForm (RJSF + MUI) into the public bundle.
import Divider from '../common/components/Divider/Divider';

// Lazy-loaded CMS components — these pull in heavy dependencies or are rarely used on the homepage,
// so they are code-split into separate chunks to reduce initial JS payload.
// IMPORTANT: These lazy components must NOT be rendered during SSR (renderToString is synchronous).
// During SSR, their Suspense fallback (a placeholder div) will be rendered instead.
const ArticleFilters = lazy(() =>
  import('../article/components/ArticleFilters/ArticleFilters').then((m) => ({ default: m.ArticleFilters }))
);
const RichTextPreview = lazy(() =>
  import('../article/components/RichTextEditor/RichTextPreview').then((m) => ({ default: m.RichTextPreview }))
);
const SocialNetworks = lazy(() =>
  import('../league/components/SocialNetworks/SocialNetworks').then((m) => ({ default: m.SocialNetworks }))
);
const FeaturedTags = lazy(() =>
  import('../article/components/FeaturedTags/FeaturedTags').then((m) => ({ default: m.FeaturedTags }))
);
const ClubList = lazy(() => import('../league/components/ClubList').then((m) => ({ default: m.ClubList })));
const NewsletterSubscribe = lazy(() =>
  import('../article/components/NewsletterSubscribe').then((m) => ({ default: m.NewsletterSubscribe }))
);
const TeamList = lazy(() => import('../league/components/TeamList/TeamList').then((m) => ({ default: m.TeamList })));
import { Tabs } from '../tabs/components/Tabs/Tabs';
const VideoList = lazy(() => import('../video/components/VideoList/VideoList').then((m) => ({ default: m.VideoList })));
const VideoFilters = lazy(() =>
  import('../video/components/VideoFilters/VideoFilters').then((m) => ({ default: m.VideoFilters }))
);
const VideoCarousel = lazy(() =>
  import('../video/components/VideoCarousel/VideoCarousel').then((m) => ({ default: m.VideoCarousel }))
);
const PpvVideoCarousel = lazy(() =>
  import('../ppvVideo/components/PpvVideoCarousel/PpvVideoCarousel').then((m) => ({ default: m.PpvVideoCarousel }))
);
const PlayersStatsTable = lazy(() =>
  import('../player/components/PlayersStatsTable/PlayersStatsTable').then((m) => ({ default: m.PlayersStatsTable }))
);
const AttendanceCard = lazy(() =>
  import('../attendance/components/AttendanceCard/AttendanceCard').then((m) => ({ default: m.AttendanceCard }))
);
const AttendanceTable = lazy(() =>
  import('../attendance/components/AttendanceTable/AttendanceTable').then((m) => ({ default: m.AttendanceTable }))
);
const PhotoAlbums = lazy(() =>
  import('../photoAlbums/components/PhotoAlbums/PhotoAlbums').then((m) => ({ default: m.PhotoAlbums }))
);
const Faq = lazy(() => import('../faq/components/Faq/Faq').then((m) => ({ default: m.Faq })));
const ContactUs = lazy(() =>
  import('@ui/common/components/ContactUs/ContactUs').then((m) => ({ default: m.ContactUs }))
);
const ContactsList = lazy(() =>
  import('../league/components/ContactsList/ContactsList').then((m) => ({ default: m.default }))
);
const ArticleCarousel = lazy(() =>
  import('../article/components/ArticleCarousel/ArticleCarousel').then((m) => ({ default: m.ArticleCarousel }))
);
const SponsorsCarousel = lazy(() =>
  import('../league/components/SponsorsCarousel/SponsorsCarousel').then((m) => ({ default: m.SponsorsCarousel }))
);
const LeagueTable = lazy(() => import('../league/components/LeagueTable/LeagueTable'));
const Stats = lazy(() => import('../stats/components/Stats/Stats').then((m) => ({ default: m.Stats })));
const Engagement = lazy(() =>
  import('../engagement/components/Engagement/Engagement').then((m) => ({ default: m.Engagement }))
);
const EngagementCarousel = lazy(() =>
  import('../engagement/components/EngagementCarousel/EngagementCarousel').then((m) => ({
    default: m.EngagementCarousel,
  }))
);
const EngagementHistory = lazy(() =>
  import('../engagement/components/EngagementHistory/EngagementHistory').then((m) => ({
    default: m.EngagementHistory,
  }))
);
const EngagementTeaserBlock = lazy(() =>
  import('../engagement/components/EngagementTeaser/EngagementTeaserBlock').then((m) => ({
    default: m.EngagementTeaserBlock,
  }))
);
const SeasonFilter = lazy(() =>
  import('../common/components/SeasonFilter/SeasonFilter').then((m) => ({ default: m.SeasonFilter }))
);
const SectionJumpNav = lazy(() =>
  import('../common/components/SectionJumpNav/SectionJumpNav').then((m) => ({ default: m.SectionJumpNav }))
);
const ImportantDatesList = lazy(() =>
  import('../league/components/ImportantDatesList/ImportantDatesList').then((m) => ({
    default: m.ImportantDatesList,
  }))
);
const TeamSquad = lazy(() => import('../club/components/TeamSquad/TeamSquad').then((m) => ({ default: m.TeamSquad })));

export const cmsWebComponentsMap = {
  [CmsComponent.ArticleCarousel]: ArticleCarousel,
  [CmsComponent.ArticleCard]: ArticleCardBlock,
  [CmsComponent.ArticleFilters]: ArticleFilters,
  [CmsComponent.ArticleHighlightedCard]: ArticleHighlightedCard,
  [CmsComponent.ArticleList]: ArticleList,
  [CmsComponent.RichTextPreview]: RichTextPreview,
  [CmsComponent.NewsletterSubscribe]: NewsletterSubscribe,
  [CmsComponent.LeagueTable]: LeagueTable,
  [CmsComponent.FixtureList]: FixtureList,
  [CmsComponent.ClubList]: ClubList,
  [CmsComponent.TeamList]: TeamList,
  [CmsComponent.SocialNetworks]: SocialNetworks,
  [CmsComponent.SponsorCard]: SponsorCard,
  [CmsComponent.SponsorsGrid]: SponsorsGrid,
  [CmsComponent.SponsorsCarousel]: SponsorsCarousel,
  [CmsComponent.Ad]: Ad,
  [CmsComponent.FeaturedTags]: FeaturedTags,
  [CmsComponent.Link]: Link,
  [CmsComponent.ContactsList]: ContactsList,
  [CmsComponent.Faq]: Faq,
  [CmsComponent.ContactUs]: ContactUs,
  [CmsComponent.Tabs]: Tabs,
  [CmsComponent.VideoList]: VideoList,
  [CmsComponent.VideoFilters]: VideoFilters,
  [CmsComponent.VideoCarousel]: VideoCarousel,
  [CmsComponent.PlayersStatsTable]: PlayersStatsTable,
  [CmsComponent.AttendanceCard]: AttendanceCard,
  [CmsComponent.AttendanceTable]: AttendanceTable,
  [CmsComponent.PhotoAlbums]: PhotoAlbums,
  [CmsComponent.PpvVideoCarousel]: PpvVideoCarousel,
  [CmsComponent.Stats]: Stats,
  [CmsComponent.Engagement]: Engagement,
  [CmsComponent.EngagementCarousel]: EngagementCarousel,
  [CmsComponent.EngagementHistory]: EngagementHistory,
  [CmsComponent.EngagementTeaser]: EngagementTeaserBlock,
  [CmsComponent.SeasonFilter]: SeasonFilter,
  [CmsComponent.SectionJumpNav]: SectionJumpNav,
  [CmsComponent.ImportantDatesList]: ImportantDatesList,
  [CmsComponent.TeamSquad]: TeamSquad,
  [CmsComponent.Image]: ImageBlock,
  [CmsComponent.Divider]: Divider,
};
